import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../components/atlas/helpers";
import { TrendingUp, TrendingDown, Activity, Clock, ShieldCheck, Zap, Crosshair, Target } from "lucide-react";

export function FuturesWorkspace() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [livePrice, setLivePrice] = useState(0);

  // Connect to Binance WebSocket for real-time tick data (bypasses CORS)
  useEffect(() => {
    const ws = new WebSocket("wss://stream.binance.com:9443/ws/btcusdt@trade");
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data && data.p) {
          setLivePrice(parseFloat(data.p));
        }
      } catch (e) {
        // ignore
      }
    };

    return () => {
      ws.close();
    };
  }, []);

  useEffect(() => {
    let active = true;
    const fetchState = async () => {
      try {
        const res = await axios.get(`${API}/futures/paper-state`);
        if (active) {
          setState(res.data);
          setError(null);
          setLoading(false);
        }
      } catch (err) {
        if (active) {
          setError(err.message || "Failed to fetch state");
          setLoading(false);
        }
      }
    };

    fetchState();
    const interval = setInterval(fetchState, 5000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-950">
        <div className="flex flex-col items-center animate-pulse">
          <Activity className="w-10 h-10 text-blue-500 mb-4 animate-spin" />
          <span className="text-gray-400 font-medium tracking-widest text-sm">INITIALIZING MACRO ENGINE...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-950">
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-8 max-w-md text-center backdrop-blur-md shadow-2xl shadow-red-900/20">
          <ShieldCheck className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-red-400 font-semibold mb-2">Connection Offline</h2>
          <p className="text-red-300/70 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const {
    cash, realized_pnl, n_wins, n_losses, open_position, equity_snapshot, trades, metrics, telemetry
  } = state || {};

  const totalTrades = (n_wins || 0) + (n_losses || 0);
  const winRate = totalTrades > 0 ? ((n_wins / totalTrades) * 100).toFixed(1) : 0.0;
  
  const fUsd = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
  const fPct = (val) => `${(val > 0 ? '+' : '')}${(val || 0).toFixed(2)}%`;
  const fNum = (val, dec=2) => Number(val || 0).toFixed(dec);

  const telemetryPrice = telemetry?.price || 0;
  const curPrice = livePrice > 0 ? livePrice : telemetryPrice;

  // Calculate real-time unrealized PnL using the live price
  let realTimeUnrealized = equity_snapshot?.unrealized_pnl || 0;
  if (open_position && curPrice > 0) {
    const pnlPct = open_position.direction * (curPrice - open_position.entry_price) / open_position.entry_price;
    realTimeUnrealized = pnlPct * open_position.size_usd;
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-[#0a0c10] text-gray-100 p-8" style={{ background: "radial-gradient(circle at top left, #12192b 0%, #0a0c10 100%)" }}>
      
      {/* Header */}
      <div className="flex justify-between items-end mb-8 border-b border-gray-800/50 pb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-500/20 p-2 rounded-lg">
              <Activity className="w-6 h-6 text-blue-400" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-300">
              Atlas Futures Engine
            </h1>
          </div>
          <p className="text-gray-400 text-sm pl-12 tracking-wide uppercase">12-Hour Macro Prediction Core</p>
        </div>
        
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 bg-gray-900/80 px-4 py-2 rounded-full border border-gray-800 shadow-xl backdrop-blur-md">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-widest">Daemon Live</span>
          </div>
          {curPrice > 0 && (
            <div className="text-gray-500 font-mono text-xs tracking-wider">
              BTC: {fUsd(curPrice)}
            </div>
          )}
        </div>
      </div>

      {/* Hero Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
        <MetricCard label="Current Equity" value={fUsd(equity_snapshot?.equity)} icon={<Zap />} color="text-indigo-400" />
        <MetricCard label="Realized PnL" value={fUsd(realized_pnl)} icon={realized_pnl >= 0 ? <TrendingUp /> : <TrendingDown />} color={realized_pnl >= 0 ? "text-emerald-400" : "text-rose-400"} />
        <MetricCard label="Win Rate" value={`${winRate}%`} icon={<ShieldCheck />} color="text-blue-400" />
        <MetricCard label="Sharpe Ratio" value={fNum(metrics?.sharpe)} icon={<Target />} color="text-amber-400" />
        <MetricCard label="Max Drawdown" value={fPct(metrics?.mdd)} icon={<TrendingDown />} color="text-red-400" />
        <MetricCard label="Total Trades" value={totalTrades} icon={<Clock />} color="text-purple-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Active Position & Telemetry */}
        <div className="lg:col-span-1 flex flex-col gap-6">
          
          {/* Active Position Panel */}
          <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-6 backdrop-blur-xl shadow-2xl relative overflow-hidden group">
            <div className={`absolute -inset-20 opacity-10 blur-[80px] transition-all duration-1000 ${open_position ? (open_position.direction === 1 ? 'bg-emerald-500' : 'bg-rose-500') : 'bg-blue-500'}`}></div>

            <h3 className="text-sm uppercase tracking-widest text-gray-400 font-semibold mb-6 relative z-10 flex items-center gap-2">
              <Crosshair className="w-4 h-4" /> Active Position
            </h3>
            
            {open_position ? (
              <div className="relative z-10 space-y-6">
                <div className="flex justify-between items-end border-b border-white/5 pb-4">
                  <div>
                    <span className={`text-3xl font-black tracking-widest block mb-1 ${open_position.direction === 1 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {open_position.direction === 1 ? 'LONG' : 'SHORT'}
                    </span>
                    <span className="text-gray-400 font-mono text-sm">
                      {fNum(open_position.size_usd / open_position.entry_price, 3)} BTC
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="bg-gray-800 text-gray-300 text-xs px-3 py-1 rounded-full font-mono mb-1">{open_position.bars_held} bars</div>
                    <div className="text-gray-500 text-xs">Notional: {fUsd(open_position.size_usd)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-black/30 rounded-xl p-3 border border-white/5">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Entry Price</div>
                    <div className="font-mono text-base">{fUsd(open_position.entry_price)}</div>
                  </div>
                  <div className="bg-black/30 rounded-xl p-3 border border-white/5 relative overflow-hidden">
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${equity_snapshot?.unrealized_pnl >= 0 ? 'bg-emerald-500/50' : 'bg-rose-500/50'}`}></div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 pl-2">Current</div>
                    <div className="font-mono text-base pl-2">{fUsd(curPrice)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-red-500/5 rounded-xl p-3 border border-red-500/10 text-rose-300">
                    <div className="text-[10px] opacity-70 uppercase tracking-wider mb-1">Stop Loss</div>
                    <div className="font-mono text-sm">{fUsd(open_position.sl_price)}</div>
                  </div>
                  <div className="bg-emerald-500/5 rounded-xl p-3 border border-emerald-500/10 text-emerald-300">
                    <div className="text-[10px] opacity-70 uppercase tracking-wider mb-1">Take Profit</div>
                    <div className="font-mono text-sm">{fUsd(open_position.tp_price)}</div>
                  </div>
                </div>

                <div className="bg-black/50 rounded-xl p-4 border border-white/5 text-center mt-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center justify-center gap-1">
                    Unrealized PnL <span className="relative flex h-2 w-2 ml-1"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span></span>
                  </div>
                  <div className={`font-mono text-3xl font-bold tracking-tight transition-colors ${realTimeUnrealized >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {fUsd(realTimeUnrealized)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative z-10 h-full flex flex-col items-center justify-center min-h-[250px] opacity-60">
                <div className="relative">
                  <div className="absolute -inset-4 rounded-full border border-blue-500/30 animate-[ping_3s_ease-in-out_infinite]"></div>
                  <div className="absolute -inset-8 rounded-full border border-blue-500/10 animate-[ping_4s_ease-in-out_infinite] delay-75"></div>
                  <Activity className="w-12 h-12 text-blue-400 mb-6 relative z-10" />
                </div>
                <div className="text-gray-400 tracking-widest text-sm font-medium mt-4">SEARCHING FOR MACRO EDGE...</div>
              </div>
            )}
          </div>

          {/* Model Telemetry Panel */}
          <div className="bg-gray-900/40 border border-gray-800/80 rounded-2xl p-6 shadow-lg relative">
            <h3 className="text-sm uppercase tracking-widest text-gray-400 font-semibold mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" /> AI Telemetry
            </h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>P(UP) Confidence</span>
                  <span className="font-mono text-blue-300">{fNum(telemetry?.prob_up, 3)}</span>
                </div>
                <div className="h-1.5 w-full bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-400 transition-all duration-500" 
                    style={{ width: `${Math.min(100, Math.max(0, (telemetry?.prob_up || 0.5) * 100))}%` }}
                  ></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="bg-black/40 rounded-lg p-3 border border-white/5">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">VPIN (Toxicity)</div>
                  <div className={`font-mono text-sm ${(telemetry?.vpin || 0) > 0.7 ? 'text-amber-400' : 'text-gray-300'}`}>
                    {fNum(telemetry?.vpin, 3)}
                  </div>
                </div>
                <div className="bg-black/40 rounded-lg p-3 border border-white/5">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Hurst Exponent</div>
                  <div className={`font-mono text-sm ${(telemetry?.hurst || 0.5) > 0.55 ? 'text-emerald-400' : 'text-gray-300'}`}>
                    {fNum(telemetry?.hurst, 3)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trade History */}
        <div className="lg:col-span-2">
          <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-6 backdrop-blur-xl shadow-2xl h-full flex flex-col">
            <h3 className="text-sm uppercase tracking-widest text-gray-400 font-semibold mb-6 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Execution Log
            </h3>
            
            <div className="flex-grow overflow-auto pr-2 custom-scrollbar">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-500 text-xs tracking-wider uppercase">
                    <th className="pb-4 font-medium">Time</th>
                    <th className="pb-4 font-medium">Symbol</th>
                    <th className="pb-4 font-medium">Dir</th>
                    <th className="pb-4 font-medium text-right">Size (BTC)</th>
                    <th className="pb-4 font-medium text-right">Lev.</th>
                    <th className="pb-4 font-medium text-right">Entry</th>
                    <th className="pb-4 font-medium text-right">SL</th>
                    <th className="pb-4 font-medium text-right">TP</th>
                    <th className="pb-4 font-medium text-right">Close</th>
                    <th className="pb-4 font-medium text-right">Net PnL</th>
                  </tr>
                </thead>
                <tbody className="text-sm font-mono">
                  {trades && trades.length > 0 ? (
                    [...trades].reverse().slice(0, 15).map((t, idx) => (
                      <tr key={idx} className="border-b border-gray-800/50 hover:bg-white/5 transition-colors group">
                        <td className="py-4 text-gray-400 text-xs">{new Date(t.timestamp).toLocaleTimeString()}</td>
                        <td className="py-4">{t.symbol}</td>
                        <td className="py-4">
                          <span className={`px-2 py-1 rounded text-[10px] font-bold ${t.direction === 'LONG' || t.direction === 1 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                            {t.direction === 1 ? 'LONG' : (t.direction === -1 ? 'SHORT' : t.direction)}
                          </span>
                        </td>
                        <td className="py-4 text-right text-gray-400">
                          {t.size_usd && t.entry_price ? fNum(t.size_usd / t.entry_price, 3) : '-'}
                        </td>
                        <td className="py-4 text-right text-gray-500 text-xs">
                          {t.leverage ? `${t.leverage}x` : '-'}
                        </td>
                        <td className="py-4 text-right text-gray-300">{fUsd(t.entry_price)}</td>
                        <td className="py-4 text-right text-rose-300/60 text-xs">{t.sl_price ? fUsd(t.sl_price) : '-'}</td>
                        <td className="py-4 text-right text-emerald-300/60 text-xs">{t.tp_price ? fUsd(t.tp_price) : '-'}</td>
                        <td className="py-4 text-right text-gray-300">{fUsd(t.close_price)}</td>
                        <td className={`py-4 text-right font-bold ${t.pnl_usd >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {fUsd(t.pnl_usd)} <span className="text-[10px] opacity-70 ml-1">({fPct(t.return_pct)})</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="py-12 text-center text-gray-500 italic">No execution history available yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(0,0,0,0.2); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
      `}} />
    </div>
  );
}

function MetricCard({ label, value, icon, color }) {
  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-4 backdrop-blur-xl shadow-lg relative overflow-hidden group hover:border-gray-700 transition-colors">
      <div className={`absolute top-0 right-0 p-3 opacity-20 group-hover:opacity-100 transition-opacity ${color}`}>
        {React.cloneElement(icon, { className: "w-6 h-6" })}
      </div>
      <div className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold mb-1 relative z-10">{label}</div>
      <div className={`text-xl font-bold tracking-tight relative z-10 ${color}`}>{value}</div>
    </div>
  );
}
